"use client";

import { useState, useMemo, useCallback } from "react";
import { Input } from "@heroui/react";
import { Check } from "lucide-react";
import { IAirdropTaskRender } from "@/types/task-renderer.types";
import { getAirdropArgumentValue } from "@/lib/challenges";
import { ChallengeCampaignEventState } from "@/types/challenges.types";
import { QuestTaskButton } from "../QuestTaskButton";

const QuizOption = ({
  label,
  isSelected,
  isDisabled,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    disabled={isDisabled}
    onClick={onClick}
    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${
      isSelected
        ? "border-[#60FF8A] bg-[#60FF8A]/10"
        : "border-gray-700/50 bg-[#151A1E] hover:bg-[#1A2025] hover:border-gray-600"
    } ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <div
      className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
        isSelected
          ? "border-[#60FF8A] bg-[#60FF8A]"
          : "border-gray-600"
      }`}
    >
      {isSelected && <Check className="w-3 h-3 text-black" />}
    </div>
    <span
      className={`text-base ${
        isSelected ? "text-[#60FF8A] font-medium" : "text-gray-200"
      }`}
    >
      {label}
    </span>
  </button>
);

export const QuizRenderer = ({
  task,
  completeTaskMutation,
  campaign,
  overrideQuestion,
}: IAirdropTaskRender) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [input, setInput] = useState<string>("");

  const campaignTask = campaign.tasks?.find(
    (campaignTask) => campaignTask.id === task.taskGuid
  );

  const options = useMemo(() => {
    return JSON.parse(
      getAirdropArgumentValue(campaignTask?.arguments ?? [], "options") ?? "[]"
    ) as string[];
  }, [campaignTask?.arguments]);

  const randomizedOptions = useMemo(
    () => [...options].sort(() => Math.random() - 0.5),
    [options]
  );

  const isMultipleChoice = useMemo(
    () =>
      (getAirdropArgumentValue(campaignTask?.arguments ?? [], "options") ?? [])
        .length > 1,
    [campaignTask?.arguments]
  );

  const isDisabled =
    task.status === ChallengeCampaignEventState.SUCCESSFUL ||
    completeTaskMutation.isPending;

  const question = useMemo(
    () => overrideQuestion || getAirdropArgumentValue(campaignTask?.arguments ?? [], "question"),
    [campaignTask?.arguments, overrideQuestion]
  );

  const onPressHandler = useCallback(() => {
    const answer = isMultipleChoice ? selectedOption : input;
    completeTaskMutation.mutateAsync({
      campaignId: campaign.id,
      groupId: campaign.campaignGroup,
      taskName: task.taskName,
      extraArguments: [answer],
      taskGuid: task.taskGuid,
    });
  }, [
    completeTaskMutation,
    campaign,
    task,
    isMultipleChoice,
    selectedOption,
    input,
  ]);

  return (
    <div className="mt-4 space-y-6">
      {question && (
        <div className="text-lg font-semibold text-white">{question}</div>
      )}

      {isMultipleChoice ? (
        <div className="flex flex-col gap-3">
          {randomizedOptions.map((option) => (
            <QuizOption
              key={option}
              label={option}
              isSelected={selectedOption === option}
              isDisabled={isDisabled}
              onClick={() => setSelectedOption(option)}
            />
          ))}
        </div>
      ) : (
        <Input
          isDisabled={isDisabled}
          value={input}
          onValueChange={setInput}
          type="text"
          placeholder="Enter the right answer"
          variant="bordered"
          classNames={{
            inputWrapper: "border-green-500/30 bg-green-900/20 hover:border-green-500/50 focus-within:border-green-500 transition-colors duration-200 rounded-2xl",
            input: "text-white placeholder:text-gray-400 px-3 py-2",
          }}
        />
      )}

      <QuestTaskButton
        buttonText="Submit Answer"
        taskMutation={completeTaskMutation}
        task={task}
        onClick={onPressHandler}
      />
    </div>
  );
};
